import { useEffect, useMemo, useState } from 'react';

const INSTALL_PROMPT_STORAGE_KEY = 'redimdwmUi_pwaInstallPrompt';
const NOT_NOW_SESSION_KEY = 'redimdwmUi_pwaInstallPromptNotNow';
const DONT_ASK_AGAIN_DAYS = 120;
const APP_VERSION = import.meta.env.VITE_REDIMDWM_APP_VERSION || 'local';

const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  const userAgent = navigator.userAgent || '';
  const hasTouchMacUserAgent = /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1;
  return (
    window.matchMedia('(max-width: 768px)').matches
    || window.matchMedia('(any-pointer: coarse)').matches
    || /Android|iPhone|iPad|iPod/i.test(userAgent)
    || hasTouchMacUserAgent
  );
};

const isStandaloneDisplay = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
};

const isIosDevice = () => {
  const userAgent = navigator.userAgent || '';
  return ((/iPad|iPhone|iPod/.test(userAgent) || (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1)) && !window.MSStream);
};

const getStoredPreference = () => {
  try {
    return JSON.parse(localStorage.getItem(INSTALL_PROMPT_STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
};

const shouldSuppressPrompt = () => {
  const preference = getStoredPreference();
  if (!preference) return false;
  if (preference.appVersion !== APP_VERSION) return false;
  return Number(preference.suppressedUntil || 0) > Date.now();
};

function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showManualHelp, setShowManualHelp] = useState(false);

  const installHelpText = useMemo(() => {
    if (typeof window === 'undefined') return false;
    if (isIosDevice()) {
      return 'Use the browser share button, then choose Add to Home Screen.';
    }
    return 'Open the browser menu, then choose Install app or Add to Home screen.';
  }, []);

  useEffect(() => {
    if (!isMobileDevice() || isStandaloneDisplay() || shouldSuppressPrompt()) return undefined;
    if (sessionStorage.getItem(NOT_NOW_SESSION_KEY) === APP_VERSION) return undefined;

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setVisible(true);
    };

    const handleAppInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      localStorage.removeItem(INSTALL_PROMPT_STORAGE_KEY);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    const promptTimer = window.setTimeout(() => setVisible(true), 1200);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.clearTimeout(promptTimer);
    };
  }, []);

  const dismissForSession = () => {
    sessionStorage.setItem(NOT_NOW_SESSION_KEY, APP_VERSION);
    setVisible(false);
    setShowManualHelp(false);
  };

  const dismissLongTerm = () => {
    const suppressedUntil = Date.now() + DONT_ASK_AGAIN_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(INSTALL_PROMPT_STORAGE_KEY, JSON.stringify({
      appVersion: APP_VERSION,
      suppressedUntil,
    }));
    setVisible(false);
    setShowManualHelp(false);
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice.catch(() => null);
      setDeferredPrompt(null);
      setVisible(false);
      return;
    }

    setShowManualHelp(true);
  };

  if (!visible || isStandaloneDisplay()) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div
        className="w-full max-w-sm rounded-md border border-primary/30 bg-white p-5 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pwa-install-title"
      >
        <div className="space-y-4">
          <div>
            <h2 id="pwa-install-title" className="text-base font-semibold text-primary">
              Add RediMD WM to your home screen
            </h2>
            <p className="mt-1 text-sm text-neutral-darkest">
              Install the app for quicker access on this device.
            </p>
            {showManualHelp && (
              <p className="mt-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                {installHelpText}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <button
              type="button"
              className="rounded-md bg-button px-3 py-2 text-sm font-medium text-white hover:bg-button-light focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              onClick={handleInstall}
            >
              OK
            </button>
            <button
              type="button"
              className="rounded-md border border-neutral px-3 py-2 text-sm font-medium text-neutral-darkest hover:bg-neutral-lightest focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              onClick={dismissForSession}
            >
              Not now
            </button>
            <button
              type="button"
              className="rounded-md border border-neutral px-3 py-2 text-sm font-medium text-neutral-darkest hover:bg-neutral-lightest focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              onClick={dismissLongTerm}
            >
              Don't ask again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PwaInstallPrompt;
